import {
  Box,
  Typography,
  Button,
  Divider,
  Stack,
  Container,
} from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useCustomMutation } from "@refinedev/core";

export const DangerZone = () => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const { translate } = useTranslation("pages.stores.settings.dangerZone");

  const { mutate: deleteAllProducts, isLoading: isDeleting } =
    useCustomMutation();

  // ⏱️ Hold config
  const HOLD_DURATION = 5000; // 5 saniye (istersen 10000 yap)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [progress, setProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);

  const remainingSeconds = Math.ceil(
    (HOLD_DURATION - (progress / 100) * HOLD_DURATION) / 1000,
  );

  // 🧹 cleanup (component unmount olursa leak olmasın)
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const startHold = () => {
    if (!selectedCountry?.id || isDeleting) return;

    setIsHolding(true);
    setProgress(0);

    const startTime = Date.now();

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const percent = Math.min((elapsed / HOLD_DURATION) * 100, 100);
      setProgress(percent);
    }, 50);

    timerRef.current = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      setIsHolding(false);
      setProgress(0);

      deleteAllProducts({
        url: `store/${selectedCountry.id}/product/delete/all`,
        method: "delete",
        values: {},
        successNotification: () => ({
          message: translate("deleteAllProducts.success"),
          type: "success",
        }),
      });
    }, HOLD_DURATION);
  };

  const cancelHold = () => {
    setIsHolding(false);
    setProgress(0);

    if (timerRef.current) clearTimeout(timerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    timerRef.current = null;
    intervalRef.current = null;
  };

  return (
    <>
      <Container sx={{ py: 3 }}>
        <Stack direction={"row"} alignItems={"center"}>
          <Typography
            variant="body2"
            fontWeight="bold"
            color="error"
            sx={{
              width: { xs: "100%", sm: "20%" },
              flexShrink: 0,
              mb: { xs: 1, sm: 0 },
            }}
          >
            {translate("deleteAllProducts.title")}:
          </Typography>

          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ flex: 1 }}
          >
            {/* Uyarı satırı */}
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box sx={{ flexShrink: 0, mt: "2px" }}></Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {translate("deleteAllProducts.description")}
                </Typography>
              </Box>
            </Box>

            {/* Hold-to-confirm butonu */}
            <Box display="flex" justifyContent="flex-end">
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 1,
                  backgroundColor: "error.main",
                }}
              >
                {/* Progress bar */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    height: "4px",
                    width: `${progress}%`,
                    backgroundColor: "rgba(255,255,255,0.55)",
                    transition: "width 0.05s linear",
                  }}
                />
                <Button
                  variant="contained"
                  color="error"
                  disabled={isDeleting || !selectedCountry}
                  onMouseDown={startHold}
                  onMouseUp={cancelHold}
                  onMouseLeave={cancelHold}
                  onTouchStart={startHold}
                  onTouchEnd={cancelHold}
                  sx={{
                    position: "relative",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isHolding
                    ? remainingSeconds +
                      " " +
                      translate("deleteAllProducts.holding")
                    : translate("deleteAllProducts.holdButton")}
                </Button>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Container>
      <Divider sx={{ margin: "10px 0px" }} />
    </>
  );
};
